import { useState, useEffect, useRef } from 'react';
import { 
  LogOut, User, Crown, Calendar, Play, BarChart3, 
  Target, Shield, Car, Brain, Fuel, Clock, Award, Trophy,
  Sparkles, Zap, CheckCircle, TrendingUp, BookOpen, Bookmark, Star,
  Flame, TrendingDown, Activity, Lightbulb, ArrowLeft,
  CreditCard, Receipt, Trash2, Settings, AlertTriangle, FileText, Mail,
  Flag, Send, Hash, Copy, RefreshCw, ChevronRight, Eye, XCircle,
  Camera, Bell, Save, Edit2, Upload
} from 'lucide-react';
import axios from 'axios';
import PerformanceInsights from './PerformanceInsights';
import ImageLightbox from './ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to fix image URLs - handles old /uploads/ and new /api/uploads/ formats
// Also handles old domain URLs that need to be redirected to current domain
const fixImageUrl = (url) => {
  if (!url) return null;
  
  // If already a full http URL
  if (url.startsWith('http')) {
    // List of old domains that need to be redirected
    const oldDomains = [
      'swedriving.preview.emergentagent.com',
      'teoriprov-3.preview.emergentagent.com',
      'teoriprov-2.preview.emergentagent.com',
      'teoriprov.preview.emergentagent.com',
      'drivingtheory-1.preview.emergentagent.com',
      'drivingtheory.preview.emergentagent.com',
      'turkswedtheory.preview.emergentagent.com',
      'svenskteori.preview.emergentagent.com',
      'ehliyet.preview.emergentagent.com',
      'swedish-driver-test.preview.emergentagent.com',
      'swedish-theory-test-1.preview.emergentagent.com',
      'swedish-theory-test.preview.emergentagent.com',
      'testprep-tr.preview.emergentagent.com',
      'testprep-tr.emergent.host',
      'ehliyet.nu'
    ];
    
    // Check if URL is from an old domain
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        // Extract the path after the domain
        try {
          const urlObj = new URL(url);
          const path = urlObj.pathname;
          // Ensure path has /api prefix
          const fixedPath = path.startsWith('/api') ? path : `/api${path}`;
          return `${BACKEND_URL}${fixedPath}`;
        } catch (e) {
          // Fallback: extract filename
          const filename = url.split('/').pop();
          return `${BACKEND_URL}/api/uploads/${filename}`;
        }
      }
    }
    
    // Fix old format URLs that don't have /api prefix
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  
  // Relative URL - ensure /api prefix
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/api/uploads/${url}`;
};

const CATEGORY_ICONS = {
  'Trafik Kuralları': Target,
  'Trafik Güvenliği': Shield,
  'Araç Bilgisi ve Manevra': Car,
  'Kişisel Koşullar': Brain,
  'Çevre': Fuel
};

const CATEGORY_COLORS = {
  'Trafik Kuralları': 'from-blue-500 to-blue-600',
  'Trafik Güvenliği': 'from-green-500 to-emerald-600',
  'Araç Bilgisi ve Manevra': 'from-purple-500 to-purple-600',
  'Kişisel Koşullar': 'from-orange-500 to-orange-600',
  'Çevre': 'from-teal-500 to-cyan-600'
};

export default function CustomerDashboard({ user, hasActiveSubscription: hasActiveSubscriptionProp, onLogout, onStartPractice, onPayment, onAdmin, onExamMode, onPremiumQuiz, isAdminImpersonating = false, onRefreshUser }) {
  // State for refreshed user data
  const [currentUser, setCurrentUser] = useState(user);
  
  // Calculate hasActiveSubscription from current user (respects premium_until expiry)
  const hasActiveSubscription = (() => {
    if (hasActiveSubscriptionProp) return true;
    if (currentUser?.is_admin) return true;
    const claimsPremium = currentUser?.is_premium || currentUser?.subscription_status === 'active';
    if (!claimsPremium) return false;
    // If a premium_until exists, enforce expiry
    if (currentUser?.premium_until) {
      const expiry = new Date(currentUser.premium_until);
      if (!isNaN(expiry.getTime()) && expiry < new Date()) {
        return false;
      }
    } else {
      // No expiry date — treat as not active (matches backend policy)
      return false;
    }
    return true;
  })();
  
  // Refresh user data on mount to get latest premium status
  useEffect(() => {
    // Ensure user data is in localStorage for API auth fallback
    if (currentUser?.email) {
      localStorage.setItem('user_email', currentUser.email);
    }
    if (currentUser?.user_id || currentUser?.id) {
      localStorage.setItem('user_id', currentUser.user_id || currentUser.id);
    }
    
    const refreshUserData = async () => {
      if (onRefreshUser) {
        const freshUser = await onRefreshUser();
        if (freshUser) {
          setCurrentUser(freshUser);
          // Update localStorage with fresh data
          if (freshUser.email) localStorage.setItem('user_email', freshUser.email);
          if (freshUser.user_id || freshUser.id) localStorage.setItem('user_id', freshUser.user_id || freshUser.id);
        }
      }
    };
    refreshUserData();
  }, [onRefreshUser]);
  
  // Update currentUser when user prop changes
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);
  
  // Debug log
  console.log('🔍 CustomerDashboard Debug:', {
    user_is_admin: currentUser?.is_admin,
    user_is_premium: currentUser?.is_premium,
    user_subscription_status: currentUser?.subscription_status,
    hasActiveSubscriptionProp,
    hasActiveSubscription
  });
  
  const [stats, setStats] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [categories, setCategories] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [examHistory, setExamHistory] = useState([]);
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeSettings, setWelcomeSettings] = useState({
    duration: 6,
    theme: "purple",
    show_confetti: true,
    show_rings: true,
    show_sparkles: true,
    title_text: "Hoş Geldiniz!",
    subtitle_text: "İsveç Ehliyet Sınavına Hazırsınız!",
    icon: "crown"
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showExamDetail, setShowExamDetail] = useState(null);
  
  // Account management states
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Profile editing states
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Notification settings states
  const [notificationSettings, setNotificationSettings] = useState({
    email_updates: true,
    exam_reminders: true,
    promotional_emails: false,
    weekly_progress: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Fetch welcome settings and show welcome screen
  useEffect(() => {
    const initWelcome = async () => {
      try {
        const response = await axios.get(`${API}/welcome-settings`);
        const settings = response.data;
        setWelcomeSettings(settings);
        
        const isAdminTest = localStorage.getItem('admin_test_mode') === 'true';
        // Check if welcome was already shown in this session
        const welcomeShownThisSession = sessionStorage.getItem('welcome_shown_this_session');
        
        if (!isAdminTest && !isAdminImpersonating && !welcomeShownThisSession) {
          setShowWelcome(true);
          sessionStorage.setItem('welcome_shown_this_session', 'true');
          // Use settings duration or default to 4 seconds
          const duration = (settings.duration || 4) * 1000;
          const timer = setTimeout(() => setShowWelcome(false), duration);
          return () => clearTimeout(timer);
        }
      } catch (error) {
        console.log('Welcome settings fetch error:', error);
        // Use defaults if fetch fails
        const isAdminTest = localStorage.getItem('admin_test_mode') === 'true';
        const welcomeShownThisSession = sessionStorage.getItem('welcome_shown_this_session');
        if (!isAdminTest && !isAdminImpersonating && !welcomeShownThisSession) {
          setShowWelcome(true);
          sessionStorage.setItem('welcome_shown_this_session', 'true');
          const timer = setTimeout(() => setShowWelcome(false), 4000);
          return () => clearTimeout(timer);
        }
      }
    };
    
    fetchAllData();
    initWelcome();
    // eslint-disable-next-line
  }, [isAdminImpersonating]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUserInfo(),
        fetchStats(),
        fetchCategories(),
        fetchUserProgress(),
        fetchCategoryStats(),
        fetchExamHistory(),
        fetchMarkedQuestions(),
        fetchPaymentHistory(),
        fetchNotificationSettings()
      ]);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/history`);
      setPaymentHistory(response.data || []);
    } catch (error) {
      console.log('Ödeme geçmişi yüklenemedi:', error);
      setPaymentHistory([]);
    }
  };
  
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'HESABIMI SİL') {
      alert('Lütfen "HESABIMI SİL" yazarak onaylayın.');
      return;
    }
    
    try {
      // Include X-User-Email header for admin impersonation mode
      const headers = {};
      if (user?.email) {
        headers['X-User-Email'] = currentUser.email;
      }
      
      await axios.delete(`${API}/user/delete-account`, { 
        withCredentials: true,
        headers 
      });
      alert('Hesabınız başarıyla silindi. GDPR kapsamında verileriniz e-postanıza gönderildi.');
      onLogout();
    } catch (error) {
      console.error('Hesap silme hatası:', error);
      alert('Hesap silinirken bir hata oluştu: ' + (error.response?.data?.detail || 'Lütfen tekrar deneyin.'));
    }
    
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  // Profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('❌ Sadece JPG, PNG, GIF veya WebP formatları kabul edilir.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ Dosya boyutu 5MB\'ı aşamaz.');
      return;
    }
    
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/user/upload-profile-photo`, formData, {
        withCredentials: true,
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-User-Email': user?.email
        }
      });
      
      if (response.data.success) {
        alert('✅ Profil fotoğrafı güncellendi!');
        fetchUserInfo();
      }
    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      alert('❌ Fotoğraf yüklenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Update profile name
  const handleUpdateProfile = async () => {
    if (!profileName.trim()) {
      alert('Lütfen geçerli bir isim girin.');
      return;
    }
    
    try {
      await axios.put(`${API}/user/profile`, { name: profileName }, { withCredentials: true });
      alert('✅ Profil güncellendi!');
      setEditingProfile(false);
      fetchUserInfo();
    } catch (error) {
      console.error('Profil güncelleme hatası:', error);
      alert('❌ Profil güncellenemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/user/notification-settings`, { withCredentials: true });
      setNotificationSettings(response.data);
    } catch (error) {
      console.log('Bildirim ayarları yüklenemedi:', error);
    }
  };

  // Update notification settings
  const handleUpdateNotifications = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    
    try {
      setSavingSettings(true);
      await axios.put(`${API}/user/notification-settings`, newSettings, { withCredentials: true });
    } catch (error) {
      console.error('Bildirim ayarları güncelenemedi:', error);
      // Revert on error
      setNotificationSettings(notificationSettings);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUserInfo(response.data);
      setProfileName(response.data.name || '');
      
      // Check if subscription expired
      if (response.data.subscription_expired) {
        setSubscriptionExpired(true);
        alert('⚠️ Aboneliğiniz sona erdi! Pratik ve sınav moduna erişim için yeni abonelik satın alın.');
      }
    } catch (error) {
      console.error('Kullanıcı bilgileri yüklenemedi:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Cache-busting ile fresh data
      const response = await axios.get(`${API}/stats?t=${Date.now()}`);
      setStats(response.data);
      console.log('📊 Customer stats updated:', response.data);
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchUserProgress = async () => {
    try {
      const response = await axios.get(`${API}/user/progress`, { withCredentials: true });
      setUserProgress(response.data);
    } catch (error) {
      console.error('İlerleme yüklenemedi:', error);
    }
  };

  const fetchCategoryStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats-by-category`, { 
        withCredentials: true,
        headers: { 'X-User-Email': user?.email }
      });
      setCategoryStats(response.data);
    } catch (error) {
      console.error('Kategori istatistikleri yüklenemedi:', error);
    }
  };

  const fetchExamHistory = async () => {
    try {
      const response = await axios.get(`${API}/user/exam-history`, { 
        withCredentials: true,
        headers: { 'X-User-Email': user?.email }
      });
      setExamHistory(response.data);
    } catch (error) {
      console.error('Sınav geçmişi yüklenemedi:', error);
    }
  };

  const fetchMarkedQuestions = async () => {
    try {
      const response = await axios.get(`${API}/user/marked-questions`, { 
        withCredentials: true,
        headers: { 'X-User-Email': user?.email }
      });
      setMarkedQuestions(response.data);
    } catch (error) {
      console.error('İşaretli sorular yüklenemedi:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // hasActiveSubscription comes from props (calculated in App.js)
  const freeQuestionsRemaining = userInfo?.free_questions_remaining || 0;

  // Check if user is defined
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-2xl font-bold mb-4">Kullanıcı bilgisi bulunamadı</p>
          <button
            onClick={() => window.location.href = '/admin'}
            className="px-6 py-3 bg-white text-red-600 rounded-lg font-bold hover:bg-gray-100"
          >
            Admin Paneline Dön
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-2xl font-bold">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Check admin impersonation from localStorage or prop
  const adminImpersonating = isAdminImpersonating || localStorage.getItem('admin_impersonating') === 'true' || localStorage.getItem('admin_test_mode') === 'true';

  const exitImpersonation = () => {
    localStorage.removeItem('admin_impersonating');
    localStorage.removeItem('original_admin');
    localStorage.removeItem('admin_test_mode');
    window.location.href = '/admin';
  };

  // ACCOUNT TAB - Render separately when active
  if (activeTab === 'account') {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Admin Impersonation Banner */}
        {adminImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-3 px-6 shadow-2xl">
            <div className="container mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="font-bold">🕵️ Admin Görünümü</span>
              </div>
              <button
                onClick={exitImpersonation}
                className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Admin Panele Dön
              </button>
            </div>
          </div>
        )}
        
        <div className={adminImpersonating ? 'pt-16' : ''}>
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute w-[500px] h-[500px] bg-gradient-to-r from-blue-500/20 to-purple-500/10 rounded-full blur-3xl top-0 left-0"></div>
            <div className="absolute w-[400px] h-[400px] bg-gradient-to-r from-indigo-500/15 to-blue-500/10 rounded-full blur-3xl bottom-0 right-0"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
            {/* Header with Avatar - Now with photo upload */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
              <div className="flex flex-col md:flex-row items-center gap-5">
                {/* Avatar with photo upload */}
                <div className="relative group">
                  {user?.profile_photo ? (
                    <img 
                      src={`${BACKEND_URL}${currentUser.profile_photo}`}
                      alt="Profil"
                      className="w-20 h-20 rounded-full object-cover shadow-lg border-2 border-white/20"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  {hasActiveSubscription && (
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-slate-800 shadow-lg">
                      <Star className="w-3.5 h-3.5 text-white fill-current" />
                    </div>
                  )}
                  {/* Photo upload overlay */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <RefreshCw className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                
                {/* User Info */}
                <div className="text-center md:text-left flex-1">
                  {editingProfile ? (
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-xl font-bold focus:outline-none focus:border-blue-400"
                        placeholder="İsminizi girin"
                      />
                      <button
                        onClick={handleUpdateProfile}
                        className="p-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingProfile(false);
                          setProfileName(user?.name || '');
                        }}
                        className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-2xl font-bold text-white">{user?.name || 'Kullanıcı'}</h1>
                      <button
                        onClick={() => {
                          setProfileName(user?.name || '');
                          setEditingProfile(true);
                        }}
                        className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-white/50 hover:text-white" />
                      </button>
                    </div>
                  )}
                  <p className="text-white/50 text-sm mb-2">{user?.email}</p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-2">
                    {hasActiveSubscription ? (
                      <span className="px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 rounded-lg text-amber-300 text-xs font-semibold flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5" /> Premium Üye
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white/50 text-xs">
                        Ücretsiz Kullanıcı
                      </span>
                    )}
                    {user?.premium_until && (
                      <span className="px-3 py-1.5 bg-green-500/15 border border-green-400/20 rounded-lg text-green-300 text-xs flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(currentUser.premium_until).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Back Button */}
                <button
                  onClick={() => setActiveTab('overview')}
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border border-white/20"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Geri
                </button>
              </div>
            </div>
            
            {/* Stats Cards - Clickable */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div 
                onClick={() => alert(`📚 Çözülen Soru Detayları\n\nToplam çözülen soru: ${userProgress?.total_questions_answered || 0}\n\nSoru bankasından pratik yaparak daha fazla soru çözebilirsiniz!`)}
                className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 backdrop-blur-lg rounded-2xl p-4 border border-blue-400/20 text-center cursor-pointer hover:scale-105 hover:border-blue-400/40 transition-all"
              >
                <div className="text-3xl font-black text-blue-400">{userProgress?.total_questions_answered || 0}</div>
                <div className="text-white/60 text-sm">📚 Çözülen Soru</div>
                <div className="text-white/30 text-xs mt-1">Detay için tıkla</div>
              </div>
              <div 
                onClick={() => alert(`✅ Doğru Cevap Detayları\n\nToplam doğru: ${userProgress?.correct_answers || 0}\nToplam yanlış: ${(userProgress?.total_questions_answered || 0) - (userProgress?.correct_answers || 0)}\n\nBaşarı oranınız: ${userProgress?.total_questions_answered > 0 ? Math.round((userProgress?.correct_answers / userProgress?.total_questions_answered) * 100) : 0}%`)}
                className="bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-lg rounded-2xl p-4 border border-green-400/20 text-center cursor-pointer hover:scale-105 hover:border-green-400/40 transition-all"
              >
                <div className="text-3xl font-black text-green-400">{userProgress?.correct_answers || 0}</div>
                <div className="text-white/60 text-sm">✅ Doğru Cevap</div>
                <div className="text-white/30 text-xs mt-1">Detay için tıkla</div>
              </div>
              <div 
                onClick={() => {
                  if (examHistory?.length > 0) {
                    const details = examHistory.map((exam, idx) => 
                      `Sınav ${idx + 1}:\n  - Tarih: ${exam.completed_at ? new Date(exam.completed_at).toLocaleDateString('tr-TR') : 'Belirtilmedi'}\n  - Doğru: ${exam.correct_answers || 0}/${exam.total_questions || 0}\n  - Başarı: ${exam.score || 0}%`
                    ).join('\n\n');
                    alert(`🏆 Sınav Geçmişi\n\nToplam ${examHistory.length} sınav tamamlandı\n\n${details}`);
                  } else {
                    alert('🏆 Sınav Geçmişi\n\nHenüz tamamlanmış sınav bulunmuyor.\n\nSınav modundan bir sınav başlatın ve tamamlayın!');
                  }
                }}
                className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 backdrop-blur-lg rounded-2xl p-4 border border-purple-400/20 text-center cursor-pointer hover:scale-105 hover:border-purple-400/40 transition-all"
              >
                <div className="text-3xl font-black text-purple-400">{userProgress?.total_exams_completed || examHistory?.length || 0}</div>
                <div className="text-white/60 text-sm">🏆 Tamamlanan Sınav</div>
                <div className="text-white/30 text-xs mt-1">Detay için tıkla</div>
              </div>
              <div 
                onClick={() => {
                  const total = userProgress?.total_questions_answered || 0;
                  const correct = userProgress?.correct_answers || 0;
                  const wrong = total - correct;
                  const rate = total > 0 ? Math.round((correct / total) * 100) : 0;
                  
                  let message = `📊 Başarı Analizi\n\n`;
                  message += `Toplam Çözülen: ${total} soru\n`;
                  message += `Doğru: ${correct} (${rate}%)\n`;
                  message += `Yanlış: ${wrong} (${100 - rate}%)\n\n`;
                  
                  if (rate >= 80) {
                    message += `🎉 Harika! Sınava hazırsınız!`;
                  } else if (rate >= 60) {
                    message += `💪 İyi gidiyorsunuz, biraz daha pratik yapın!`;
                  } else {
                    message += `📖 Daha fazla pratik yapmanız önerilir.`;
                  }
                  
                  alert(message);
                }}
                className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 backdrop-blur-lg rounded-2xl p-4 border border-orange-400/20 text-center cursor-pointer hover:scale-105 hover:border-orange-400/40 transition-all"
              >
                <div className="text-3xl font-black text-orange-400">
                  {userProgress?.total_questions_answered > 0 
                    ? Math.round((userProgress?.correct_answers / userProgress?.total_questions_answered) * 100)
                    : 0}%
                </div>
                <div className="text-white/60 text-sm">📊 Başarı Oranı</div>
                <div className="text-white/30 text-xs mt-1">Detay için tıkla</div>
              </div>
            </div>
            
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Profile Card */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-5 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-400" />
                  </div>
                  Profil Bilgilerim
                </h3>
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4 flex items-center gap-4">
                    <Mail className="w-5 h-5 text-white/50" />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider">E-posta</div>
                      <div className="text-white font-medium">{user?.email || '-'}</div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 flex items-center gap-4">
                    <Calendar className="w-5 h-5 text-white/50" />
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider">Kayıt Tarihi</div>
                      <div className="text-white font-medium">
                        {user?.created_at 
                          ? new Date(currentUser.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                          : 'Bilgi yok'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Card */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-5 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-400" />
                  </div>
                  Ödeme Yöntemi
                </h3>
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-8 bg-white/20 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">STRIPE</span>
                      </div>
                      <span className="text-white/80 text-sm">Güvenli Ödeme</span>
                    </div>
                    <div className="text-white/60 text-xs">256-bit SSL Şifreleme</div>
                  </div>
                </div>
                {!hasActiveSubscription && (
                  <button
                    onClick={onPayment}
                    className="w-full mt-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-bold hover:scale-[1.02] transition-all shadow-lg"
                  >
                    🚀 Premium&apos;a Yükselt
                  </button>
                )}
              </div>
            </div>

            {/* Payment History / Receipts - Full Width */}
            <div className="mt-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-5 flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-yellow-400" />
                </div>
                Ödeme Geçmişi
                <span className="ml-auto text-sm font-normal text-white/50">
                  {paymentHistory.filter(p => ['paid', 'completed', 'active'].includes(p.status)).length} ödeme
                </span>
              </h3>
              {paymentHistory.filter(p => ['paid', 'completed', 'active'].includes(p.status)).length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory
                    .filter(p => ['paid', 'completed', 'active'].includes(p.status))
                    .map((payment, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => {
                          let discountInfo = '';
                          if (payment.discount_code) {
                            discountInfo = `\n\n🏷️ İndirim Kodu: ${payment.discount_code}\n` +
                              `Orijinal Fiyat: ${payment.original_amount || payment.amount} ${payment.currency || 'SEK'}\n` +
                              `İndirim: %${payment.discount_percentage || 0}`;
                          }
                          alert(`📋 Ödeme Detayları\n\n` +
                            `Paket: ${payment.package_name || 'Premium Paket'}\n` +
                            `Ödenen Tutar: ${payment.amount || 0} ${payment.currency || 'SEK'}\n` +
                            `Tarih: ${payment.created_at ? new Date(payment.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}\n` +
                            `Durum: Ödendi ✅` +
                            discountInfo +
                            `${payment.expires_at ? `\n\nGeçerlilik: ${new Date(payment.expires_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}`
                          );
                        }}
                        className="bg-white/5 hover:bg-white/10 rounded-xl p-4 flex items-center justify-between cursor-pointer transition-all group border border-transparent hover:border-green-500/30"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500/30 to-emerald-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FileText className="w-6 h-6 text-green-400" />
                          </div>
                          <div>
                            <div className="text-white font-semibold group-hover:text-green-300 transition-colors">
                              {payment.package_name || 'Premium Paket'}
                            </div>
                            <div className="text-white/50 text-sm">
                              {payment.created_at 
                                ? new Date(payment.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                                : 'Tarih bilgisi yok'}
                            </div>
                            {/* Show discount code if used */}
                            {payment.discount_code && (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-xs font-semibold">
                                  🏷️ {payment.discount_code}
                                </span>
                                <span className="text-amber-400 text-xs">
                                  %{payment.discount_percentage} indirim
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            {/* Show original price if there was a discount */}
                            {payment.discount_code && payment.original_amount && payment.original_amount !== payment.amount && (
                              <div className="text-white/40 text-sm line-through">
                                {payment.original_amount} {payment.currency || 'SEK'}
                              </div>
                            )}
                            <div className="text-white font-bold text-lg">{payment.amount || 0} {payment.currency || 'SEK'}</div>
                            <div className="text-green-400 text-sm font-medium">✅ Ödendi</div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white/5 rounded-xl border border-dashed border-white/20">
                  <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Receipt className="w-10 h-10 text-white/30" />
                  </div>
                  <p className="text-white/60 text-lg font-medium">Henüz tamamlanan ödeme yok</p>
                  <p className="text-white/40 text-sm mt-2">Premium üyelik satın aldığınızda ödemeleriniz burada görünecek</p>
                </div>
              )}
            </div>

            {/* Notification Settings */}
            <div className="mt-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-5 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-purple-400" />
                </div>
                Bildirim Ayarları
                {savingSettings && (
                  <span className="ml-auto text-sm text-white/50 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Kaydediliyor...
                  </span>
                )}
              </h3>
              <div className="space-y-4">
                {/* Email Updates */}
                <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <div className="text-white font-medium">E-posta Güncellemeleri</div>
                      <div className="text-white/50 text-sm">Platform ile ilgili önemli bildirimleri al</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateNotifications('email_updates', !notificationSettings.email_updates)}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      notificationSettings.email_updates ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                      notificationSettings.email_updates ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Exam Reminders */}
                <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <div>
                      <div className="text-white font-medium">Sınav Hatırlatıcıları</div>
                      <div className="text-white/50 text-sm">Düzenli çalışma hatırlatmaları al</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateNotifications('exam_reminders', !notificationSettings.exam_reminders)}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      notificationSettings.exam_reminders ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                      notificationSettings.exam_reminders ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Weekly Progress */}
                <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <BarChart3 className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-white font-medium">Haftalık İlerleme Raporu</div>
                      <div className="text-white/50 text-sm">Her hafta ilerleme özetini e-posta ile al</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateNotifications('weekly_progress', !notificationSettings.weekly_progress)}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      notificationSettings.weekly_progress ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                      notificationSettings.weekly_progress ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Promotional Emails */}
                <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    <div>
                      <div className="text-white font-medium">Promosyon ve Kampanyalar</div>
                      <div className="text-white/50 text-sm">İndirim ve özel tekliflerden haberdar ol</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUpdateNotifications('promotional_emails', !notificationSettings.promotional_emails)}
                    className={`w-12 h-7 rounded-full transition-colors relative ${
                      notificationSettings.promotional_emails ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                      notificationSettings.promotional_emails ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Danger Zone - Delete Account */}
            <div className="mt-6 bg-red-500/10 backdrop-blur-lg rounded-xl p-5 border border-red-500/20">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                </div>
                Tehlikeli Bölge
              </h3>
              <div className="bg-red-950/30 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div>
                    <div className="text-white font-medium text-sm mb-1">Hesabımı Sil</div>
                    <div className="text-white/50 text-xs mb-3">
                      Bu işlem geri alınamaz! Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.
                    </div>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Hesabımı Kalıcı Olarak Sil
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-red-500/30 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Hesabı Silmek İstediğinize Emin Misiniz?</h3>
                <p className="text-white/60 text-sm">Bu işlem geri alınamaz. Tüm verileriniz kalıcı olarak silinecektir.</p>
              </div>
              
              <div className="mb-5">
                <label className="text-white/70 text-sm block mb-2">
                  Onaylamak için <span className="font-bold text-red-400">HESABIMI SİL</span> yazın:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="HESABIMI SİL"
                  className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-red-500"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-xl font-medium transition-all"
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'HESABIMI SİL'}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-xl font-medium transition-all"
                >
                  Evet, Sil
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 select-none" onContextMenu={(e) => e.preventDefault()}>
      {/* Admin Impersonation Banner */}
      {adminImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-3 px-6 shadow-2xl">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              <span className="font-bold">🕵️ Admin Görünümü</span>
              <span className="text-white/80">•</span>
              <span className="text-sm">Müşteri olarak görüntüleme modu aktif</span>
            </div>
            <button
              onClick={exitImpersonation}
              className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Admin Panele Dön
            </button>
          </div>
        </div>
      )}
      
      {/* Add padding if admin is impersonating */}
      <div className={adminImpersonating ? 'pt-16' : ''}>
      {/* Animated Background - Subtle */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] -top-40 -left-40"></div>
        <div className="absolute w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[120px] -top-40 -right-40"></div>
        <div className="absolute w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] -bottom-40 left-1/2 -translate-x-1/2"></div>
      </div>

      {/* Welcome Animation - Dynamic Theme */}
      {showWelcome && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center cursor-pointer overflow-hidden"
          onClick={() => setShowWelcome(false)}
          style={{
            background: welcomeSettings.theme === 'red' 
              ? 'radial-gradient(ellipse at center, rgba(185, 28, 28, 0.9) 0%, rgba(69, 10, 10, 0.98) 70%)'
              : welcomeSettings.theme === 'blue'
              ? 'radial-gradient(ellipse at center, rgba(30, 64, 175, 0.9) 0%, rgba(15, 23, 42, 0.98) 70%)'
              : welcomeSettings.theme === 'green'
              ? 'radial-gradient(ellipse at center, rgba(21, 128, 61, 0.9) 0%, rgba(15, 23, 42, 0.98) 70%)'
              : 'radial-gradient(ellipse at center, rgba(88, 28, 135, 0.8) 0%, rgba(15, 23, 42, 0.98) 70%)'
          }}
        >
          {/* Animated confetti particles */}
          {welcomeSettings.show_confetti && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(25)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: `${6 + Math.random() * 10}px`,
                    height: `${6 + Math.random() * 10}px`,
                    backgroundColor: welcomeSettings.theme === 'red'
                      ? ['#FFFFFF', '#DC2626', '#FCA5A5', '#FFFFFF', '#DC2626'][i % 5]
                      : welcomeSettings.theme === 'blue'
                      ? ['#3B82F6', '#60A5FA', '#FFFFFF', '#1D4ED8', '#93C5FD'][i % 5]
                      : welcomeSettings.theme === 'green'
                      ? ['#10B981', '#34D399', '#FFFFFF', '#059669', '#6EE7B7'][i % 5]
                      : ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'][i % 5],
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${4 + Math.random() * 3}s`,
                    opacity: 0.7
                  }}
                />
              ))}
              
              {/* Glowing orbs */}
              <div className={`absolute top-1/4 left-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse ${
                welcomeSettings.theme === 'red' ? 'bg-red-500/30' :
                welcomeSettings.theme === 'blue' ? 'bg-blue-500/30' :
                welcomeSettings.theme === 'green' ? 'bg-green-500/30' : 'bg-blue-500/30'
              }`}></div>
              <div className={`absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse ${
                welcomeSettings.theme === 'red' ? 'bg-white/20' :
                welcomeSettings.theme === 'blue' ? 'bg-cyan-500/30' :
                welcomeSettings.theme === 'green' ? 'bg-emerald-500/30' : 'bg-purple-500/30'
              }`} style={{animationDelay: '0.5s'}}></div>
            </div>
          )}
          
          <div className="relative text-center px-4 animate-fade-in-up">
            {/* Animated Icon Container */}
            <div className="relative mb-8 mx-auto" style={{width: '180px', height: '180px'}}>
              {/* Sparkle effects */}
              {welcomeSettings.show_sparkles && (
                <>
                  <div className="absolute -top-4 -left-4 text-4xl animate-ping" style={{animationDuration: '2s'}}>✨</div>
                  <div className="absolute -top-2 -right-6 text-3xl animate-ping" style={{animationDelay: '0.3s', animationDuration: '2s'}}>⭐</div>
                  <div className="absolute -bottom-2 -left-6 text-3xl animate-ping" style={{animationDelay: '0.6s', animationDuration: '2s'}}>🌟</div>
                  <div className="absolute -bottom-4 -right-4 text-4xl animate-ping" style={{animationDelay: '0.9s', animationDuration: '2s'}}>💫</div>
                </>
              )}
              
              {/* Rotating rings */}
              {welcomeSettings.show_rings && (
                <>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div 
                      className="w-44 h-44 rounded-full animate-spin"
                      style={{
                        animationDuration: '5s',
                        background: welcomeSettings.theme === 'red'
                          ? 'conic-gradient(from 0deg, transparent, #DC2626, #FFFFFF, #DC2626, transparent)'
                          : welcomeSettings.theme === 'blue'
                          ? 'conic-gradient(from 0deg, transparent, #3B82F6, #60A5FA, #3B82F6, transparent)'
                          : welcomeSettings.theme === 'green'
                          ? 'conic-gradient(from 0deg, transparent, #10B981, #34D399, #10B981, transparent)'
                          : 'conic-gradient(from 0deg, transparent, #3B82F6, transparent, #8B5CF6, transparent, #EC4899, transparent)'
                      }}
                    ></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div 
                      className="w-36 h-36 rounded-full border-4 border-dashed border-white/30 animate-spin"
                      style={{animationDuration: '4s', animationDirection: 'reverse'}}
                    ></div>
                  </div>
                </>
              )}
              
              {/* Center icon with glow */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className={`absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse ${
                    welcomeSettings.theme === 'red' ? 'bg-gradient-to-br from-red-400 to-red-600' :
                    welcomeSettings.theme === 'blue' ? 'bg-gradient-to-br from-blue-400 to-blue-600' :
                    welcomeSettings.theme === 'green' ? 'bg-gradient-to-br from-green-400 to-green-600' :
                    'bg-gradient-to-br from-yellow-400 to-orange-500'
                  }`}></div>
                  <div className={`relative w-28 h-28 rounded-full flex items-center justify-center shadow-2xl ${
                    welcomeSettings.theme === 'red' ? 'bg-gradient-to-br from-red-500 via-red-600 to-red-700' :
                    welcomeSettings.theme === 'blue' ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700' :
                    welcomeSettings.theme === 'green' ? 'bg-gradient-to-br from-green-500 via-green-600 to-green-700' :
                    'bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500'
                  }`}>
                    {welcomeSettings.icon === 'star' ? <Star className="w-14 h-14 text-white drop-shadow-lg" /> :
                     welcomeSettings.icon === 'trophy' ? <Trophy className="w-14 h-14 text-white drop-shadow-lg" /> :
                     welcomeSettings.icon === 'car' ? <Car className="w-14 h-14 text-white drop-shadow-lg" /> :
                     <Crown className="w-14 h-14 text-white drop-shadow-lg" />}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Welcome Text with animated gradient */}
            <h2 
              className="text-5xl md:text-6xl font-black mb-6"
              style={{
                background: welcomeSettings.theme === 'red'
                  ? 'linear-gradient(90deg, #FFFFFF, #FCA5A5, #FFFFFF, #DC2626, #FFFFFF)'
                  : welcomeSettings.theme === 'blue'
                  ? 'linear-gradient(90deg, #FFFFFF, #93C5FD, #FFFFFF, #3B82F6, #FFFFFF)'
                  : welcomeSettings.theme === 'green'
                  ? 'linear-gradient(90deg, #FFFFFF, #6EE7B7, #FFFFFF, #10B981, #FFFFFF)'
                  : 'linear-gradient(90deg, #60A5FA, #A78BFA, #F472B6, #60A5FA)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient 3s linear infinite'
              }}
            >
              {welcomeSettings.title_text} 🎉
            </h2>
            
            {/* User name with glow effect */}
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-white/20 blur-xl rounded-full"></div>
              <p className="relative text-3xl md:text-4xl font-bold text-white">
                {currentUser.name}
              </p>
            </div>
            
            {/* Subtitle */}
            <p className="text-white/70 text-xl mb-8">
              🚗 {welcomeSettings.subtitle_text} 🇸🇪
            </p>
            
            {/* Animated progress bar */}
            <div className="w-56 h-2 bg-white/20 rounded-full mx-auto mb-6 overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  welcomeSettings.theme === 'red' ? 'bg-gradient-to-r from-red-400 via-white to-red-400' :
                  welcomeSettings.theme === 'blue' ? 'bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400' :
                  welcomeSettings.theme === 'green' ? 'bg-gradient-to-r from-green-400 via-emerald-400 to-green-400' :
                  'bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                }`}
                style={{
                  animation: `loading ${welcomeSettings.duration}s ease-out forwards`,
                  width: '0%'
                }}
              ></div>
            </div>
            
            {/* Bouncing dots */}
            <div className="flex justify-center gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded-full animate-bounce ${
                    welcomeSettings.theme === 'red' ? (i % 2 === 0 ? 'bg-white' : 'bg-red-400') :
                    welcomeSettings.theme === 'blue' ? (i % 2 === 0 ? 'bg-blue-400' : 'bg-cyan-400') :
                    welcomeSettings.theme === 'green' ? (i % 2 === 0 ? 'bg-green-400' : 'bg-emerald-400') :
                    ['bg-blue-400', 'bg-purple-400', 'bg-pink-400'][i]
                  }`}
                  style={{animationDelay: `${i * 0.1}s`}}
                ></div>
              ))}
            </div>
            
            {/* Click to skip hint */}
            <p className="text-white/40 text-sm animate-pulse">
              Devam etmek için tıklayın
            </p>
          </div>
          
          {/* CSS Animations */}
          <style>{`
            @keyframes gradient {
              0% { background-position: 0% center; }
              100% { background-position: 200% center; }
            }
            @keyframes loading {
              0% { width: 0%; }
              100% { width: 100%; }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
              50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
            }
            @keyframes fade-in-up {
              0% { opacity: 0; transform: translateY(30px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            .animate-float { animation: float 4s ease-in-out infinite; }
            .animate-fade-in-up { animation: fade-in-up 0.5s ease-out forwards; }
          `}</style>
        </div>
      )}

      {/* Main Content - Centered with max-width */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header - Compact & Modern */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/30">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Merhaba, {currentUser.name}!
              </h1>
              <p className="text-white/50 text-sm">İsveç Ehliyet Teorisi Hazırlığı</p>
            </div>
          </div>
          <div className="flex gap-3">
            {currentUser.is_admin && (
              <button
                onClick={onAdmin}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 shadow-lg flex items-center gap-2 hover:scale-105"
              >
                <Crown className="w-4 h-4" />
                Admin
              </button>
            )}
            <button
              onClick={onLogout}
              className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 backdrop-blur-sm flex items-center gap-2 hover:scale-105 border border-white/10"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </button>
          </div>
        </div>

        {/* Premium Banner - REMOVED as per user request */}

        {/* Success Statistics & Recommendations */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Quick Stats - Compact & Modern */}
          <div className="lg:col-span-2 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              İlerleme Durumunuz
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div 
                onClick={() => alert(`📚 Çözülen Soru Detayları\n\nToplam çözülen: ${userProgress?.total_questions_answered || 0} soru\n\nSoru bankasından pratik yaparak daha fazla soru çözebilirsiniz!`)}
                className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 text-center cursor-pointer hover:scale-105 transition-all border border-blue-400/20 group"
              >
                <div className="text-4xl font-bold text-blue-400 mb-2 group-hover:scale-110 transition-transform">{userProgress?.total_questions_answered || 0}</div>
                <div className="text-white font-medium text-sm">📚 Çözülen</div>
              </div>
              <div 
                onClick={() => alert(`✅ Doğru Cevap Detayları\n\nToplam doğru: ${userProgress?.correct_answers || 0}\nToplam yanlış: ${(userProgress?.total_questions_answered || 0) - (userProgress?.correct_answers || 0)}\n\nBaşarı oranı: ${userProgress?.accuracy_percentage || 0}%`)}
                className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 rounded-xl p-4 text-center cursor-pointer hover:scale-105 transition-all border border-green-400/20 group"
              >
                <div className="text-4xl font-bold text-green-400 mb-2 group-hover:scale-110 transition-transform">{userProgress?.correct_answers || 0}</div>
                <div className="text-white font-medium text-sm">✅ Doğru</div>
              </div>
              <div 
                onClick={() => {
                  const rate = userProgress?.accuracy_percentage || 0;
                  let status = rate >= 75 ? '🎉 Sınava hazırsınız!' : rate >= 60 ? '💪 Biraz daha!' : '📖 Pratik yapın';
                  alert(`📊 Başarı Analizi\n\nBaşarı Oranı: %${rate}\n\nGeçme sınırı: %75\n\n${status}`);
                }}
                className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-xl p-4 text-center cursor-pointer hover:scale-105 transition-all border border-amber-400/20 group"
              >
                <div className={`text-4xl font-bold mb-2 group-hover:scale-110 transition-transform ${
                  (userProgress?.accuracy_percentage || 0) >= 75 ? 'text-green-400' : 
                  (userProgress?.accuracy_percentage || 0) >= 60 ? 'text-amber-400' : 'text-orange-400'
                }`}>{userProgress?.accuracy_percentage || 0}%</div>
                <div className="text-white font-medium text-sm">📊 Başarı</div>
              </div>
            </div>
            {/* Progress Bar - Compact */}
            <div className="mt-6">
              <div className="flex justify-between text-sm text-white/50 mb-2">
                <span>Sınava Hazırlık</span>
                <span className={`font-semibold ${
                  (userProgress?.accuracy_percentage || 0) >= 75 ? 'text-green-400' : 'text-amber-400'
                }`}>{userProgress?.accuracy_percentage || 0}% / 75%</span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    (userProgress?.accuracy_percentage || 0) >= 75 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-500'
                  }`}
                  style={{ width: `${Math.min((userProgress?.accuracy_percentage || 0) / 75 * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Tip Box - Compact */}
          <div className="bg-gradient-to-br from-purple-500/20 to-indigo-600/10 rounded-2xl p-6 border border-purple-400/20 flex flex-col justify-center">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <div className="text-white font-semibold text-base mb-2">💡 Başarı İpucu</div>
                <div className="text-white/70 text-sm leading-relaxed">
                  Sınavda başarılı olmak için <strong className="text-amber-400">en az %75</strong> doğru cevap gerekiyor.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/10 rounded-lg text-white/70 text-xs">📚 Her gün 20 soru</span>
                  <span className="px-3 py-1 bg-white/10 rounded-lg text-white/70 text-xs">🔄 Tekrar</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs - BIGGER */}
        {/* Navigation Tabs - Compact */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-thin">
          <button
            onClick={() => {
              setActiveTab('overview');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'overview'
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Genel Bakış
          </button>
          <button
            onClick={() => {
              setActiveTab('exams');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'exams'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <Trophy className="w-4 h-4" />
            🏆 Sınav Geçmişi
          </button>
          <button
            onClick={() => {
              setActiveTab('insights');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'insights'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            📊 Performans
          </button>
          <button
            onClick={() => {
              setActiveTab('categories');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'categories'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <Target className="w-4 h-4" />
            🎯 Kategoriler
          </button>
          {hasActiveSubscription && (
            <button
              onClick={() => {
                setActiveTab('allQuestions');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'allQuestions'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg scale-105'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              📚 Soru Bankası
            </button>
          )}
          <button
            onClick={() => {
              setActiveTab('marked');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'marked'
                ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <Bookmark className="w-4 h-4" />
            🔖 İşaretliler ({markedQuestions.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('account');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'account'
                ? 'bg-gradient-to-r from-gray-500 to-slate-600 text-white shadow-lg scale-105'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:scale-105'
            }`}
          >
            <Settings className="w-4 h-4" />
            ⚙️ Hesabım
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* MEGA Progress Cards */}
            {userProgress && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <BookOpen className="w-10 h-10 text-blue-400" />
                    </div>
                    <span className="text-5xl font-black text-white">{userProgress.total_questions_answered}</span>
                  </div>
                  <h3 className="text-white/80 font-bold text-lg">Çözülen Soru</h3>
                  <div className="mt-2 text-blue-400 text-sm">📚 Soru bankasından çözülen toplam soru sayısı</div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <span className="text-5xl font-black text-white">{userProgress.correct_answers}</span>
                  </div>
                  <h3 className="text-white/80 font-bold text-lg">Doğru Cevap</h3>
                  <div className="mt-2 text-green-400 text-sm">✅ Doğru cevapladığınız soru sayısı</div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-yellow-500/20 rounded-xl">
                      <TrendingUp className="w-10 h-10 text-yellow-400" />
                    </div>
                    <span className="text-5xl font-black text-white">{userProgress.accuracy_percentage}%</span>
                  </div>
                  <h3 className="text-white/80 font-bold text-lg">Başarı Oranı</h3>
                  <div className="mt-2">
                    {userProgress.accuracy_percentage >= 75 ? (
                      <span className="text-green-400 text-sm">🔥 Sınava hazırsınız! Geçme limiti: %75</span>
                    ) : userProgress.accuracy_percentage >= 60 ? (
                      <span className="text-yellow-400 text-sm">⭐ İyi gidiyorsunuz! Hedef: %75</span>
                    ) : (
                      <span className="text-orange-400 text-sm">💪 %75 hedefine ulaşmak için pratik yapın</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-purple-500/20 rounded-xl">
                      <Trophy className="w-10 h-10 text-purple-400" />
                    </div>
                    <span className="text-5xl font-black text-white">{userProgress?.total_exams_completed || examHistory.length}</span>
                  </div>
                  <h3 className="text-white/80 font-bold text-lg">Tamamlanan Sınav</h3>
                  <div className="mt-2 text-purple-400 text-sm">🎯 70 soruluk deneme sınavları</div>
                </div>
              </div>
            </div>
            )}

            {/* Motivational Message */}
            {userProgress && (
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl p-8 border border-purple-500/30 mb-8">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-purple-500/30 rounded-full">
                  <Flame className="w-12 h-12 text-purple-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white mb-2">
                    {userProgress.accuracy_percentage >= 80 
                      ? '🔥 Harikasınız! Sınava hazırsınız!'
                      : userProgress.accuracy_percentage >= 60
                      ? '⭐ Çok iyi gidiyorsunuz! Biraz daha pratik yapın.'
                      : '💪 Harika başlangıç! Pratik yapmaya devam edin.'}
                  </h3>
                  <p className="text-white/80">
                    Gerçek sınavda başarılı olmak için <strong>%74+</strong> başarı oranına ihtiyacınız var.
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* Action Buttons - MEGA */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Premium Quiz Mode Button - PREMIUM ONLY */}
              {hasActiveSubscription ? (
                <button
                  onClick={onPremiumQuiz}
                  className="relative group bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-600 hover:via-blue-600 hover:to-purple-600 text-white p-8 rounded-2xl font-black text-2xl transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
                  <div className="relative flex flex-col items-center gap-4">
                    <Zap className="w-16 h-16 drop-shadow-2xl" />
                    <div className="text-center">
                      <div className="text-3xl mb-2">⚡ Premium Quiz</div>
                      <div className="text-sm font-normal opacity-90">Tüm sorular - Özelleştirilebilir</div>
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  onClick={onPayment}
                  className="relative group bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white p-8 rounded-2xl font-black text-2xl transition-all duration-500 shadow-2xl hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] hover:scale-105 overflow-hidden border-2 border-gray-600 hover:border-purple-400"
                >
                  {/* Lock animation */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  
                  <div className="relative flex flex-col items-center gap-4">
                    <div className="relative">
                      <Zap className="w-16 h-16 drop-shadow-2xl opacity-40 group-hover:opacity-80 transition-opacity" />
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full p-2 animate-pulse">
                        <Crown className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2 opacity-60 group-hover:opacity-100 transition-opacity">🔒 Premium Quiz</div>
                      <div className="text-sm font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                        Premium ile Açılır!
                      </div>
                      <div className="text-xs mt-2 opacity-70">
                        Tüm soruları özelleştirilebilir şekilde çöz
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {hasActiveSubscription ? (
                <button
                  onClick={() => setActiveTab('allQuestions')}
                  className="relative group bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 text-white p-8 rounded-2xl font-black text-2xl transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
                  <div className="relative flex flex-col items-center gap-4">
                    <BookOpen className="w-16 h-16 drop-shadow-2xl" />
                    <div className="text-center">
                      <div className="text-3xl mb-2">📚 Soru Bankası</div>
                      <div className="text-sm font-normal opacity-90">Tüm sorulara eriş</div>
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  onClick={onPayment}
                  className="relative group bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 text-white p-8 rounded-2xl font-black text-2xl transition-all duration-500 shadow-2xl hover:shadow-[0_0_40px_rgba(99,102,241,0.6)] hover:scale-105 overflow-hidden border-2 border-gray-600 hover:border-blue-400"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  
                  <div className="relative flex flex-col items-center gap-4">
                    <div className="relative">
                      <BookOpen className="w-16 h-16 drop-shadow-2xl opacity-40 group-hover:opacity-80 transition-opacity" />
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full p-2 animate-pulse">
                        <Crown className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-2 opacity-60 group-hover:opacity-100 transition-opacity">🔒 Soru Bankası</div>
                      <div className="text-sm font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                        Premium ile Açılır!
                      </div>
                      <div className="text-xs mt-2 opacity-70">
                        2000+ soruya sınırsız erişim
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {/* Exam Mode - ALWAYS VISIBLE */}
              <button
                onClick={onExamMode}
                className="relative group bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white p-8 rounded-2xl font-black text-2xl transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
                <div className="relative flex flex-col items-center gap-4">
                  <Trophy className="w-16 h-16 drop-shadow-2xl" />
                  <div className="text-center">
                    <div className="text-3xl mb-2">🏆 Sınav Modu</div>
                    <div className="text-sm font-normal opacity-90">70 soru - 50 dakika</div>
                  </div>
                </div>
              </button>

              {/* PREMIUM SECTION - Show different content based on subscription */}
              {hasActiveSubscription ? (
                /* Premium Active Badge - Simple and clean */
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5 rounded-2xl shadow-lg flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <Crown className="w-10 h-10 text-yellow-300" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-200" />
                      <span className="text-xl font-bold">Premium Üyelik Aktif</span>
                    </div>
                    {user?.premium_until && (
                      <p className="text-white/70 text-sm mt-1">
                        Son kullanma: {new Date(currentUser.premium_until).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                /* MEGA PREMIUM BUTTON - For non-subscribed users */
                <button
                  onClick={onPayment}
                  className="relative group bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 text-white p-8 rounded-3xl font-black text-2xl transition-all duration-500 shadow-2xl hover:shadow-[0_0_50px_rgba(234,179,8,0.8)] hover:scale-110 overflow-hidden border-4 border-yellow-300"
                  style={{
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, glow 3s ease-in-out infinite'
                  }}
                >
                  {/* Animated background layers */}
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute inset-0 bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
                  
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  
                  {/* Stars animation */}
                  <div className="absolute top-2 left-4 text-2xl animate-bounce" style={{animationDelay: '0s'}}>⭐</div>
                  <div className="absolute top-4 right-8 text-xl animate-bounce" style={{animationDelay: '0.3s'}}>✨</div>
                  <div className="absolute bottom-4 left-8 text-xl animate-bounce" style={{animationDelay: '0.6s'}}>💎</div>
                  
                  <div className="relative flex flex-col items-center gap-3">
                    {/* Animated crown */}
                    <div className="relative">
                      <Crown className="w-20 h-20 drop-shadow-2xl animate-bounce text-yellow-200" />
                      <div className="absolute -top-2 -right-2 bg-red-500 rounded-full px-3 py-1 text-xs font-bold animate-pulse">
                        %20 İNDİRİM
                      </div>
                    </div>
                    
                    <div className="text-center">
                      {/* Main text with gradient */}
                      <div className="text-4xl mb-2 font-black tracking-wider" style={{
                        background: 'linear-gradient(45deg, #ffffff, #fef08a, #ffffff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundSize: '200% 200%',
                        animation: 'gradient 3s ease infinite'
                      }}>
                        🌟 PREMIUM 🌟
                      </div>
                      
                      {/* Features list */}
                      <div className="space-y-1 text-sm font-semibold">
                        <div className="flex items-center gap-2 justify-center">
                          <CheckCircle className="w-4 h-4" />
                          <span>2000+ Güncel Soru</span>
                        </div>
                        <div className="flex items-center gap-2 justify-center">
                          <CheckCircle className="w-4 h-4" />
                          <span>Sınırsız Pratik</span>
                        </div>
                        <div className="flex items-center gap-2 justify-center">
                          <CheckCircle className="w-4 h-4" />
                          <span>Gerçek Sınav Deneyimi</span>
                        </div>
                      </div>
                      
                      {/* CTA */}
                      <div className="mt-4 bg-white/20 rounded-full px-6 py-2 backdrop-blur-sm">
                        <span className="text-lg font-black">HEMEN BAŞLA →</span>
                      </div>
                      
                      {/* Price hint */}
                      <div className="mt-2 text-xs opacity-80">
                        Sadece 199 kr&apos;dan başlayan fiyatlar
                      </div>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab - MEGA ENHANCED */}
        {activeTab === 'categories' && (
          <div>
            <h2 className="text-3xl font-black text-white mb-6 flex items-center gap-3">
              <Target className="w-8 h-8 text-yellow-400" />
              Kategori Bazlı Performansınız
            </h2>
            
            {categoryStats.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 border border-white/20 text-center">
                <BookOpen className="w-20 h-20 text-white/50 mx-auto mb-4" />
                <p className="text-white/70 text-xl">Henüz hiç soru çözmediniz. Hemen başlayın!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {categoryStats && categoryStats.length > 0 && categoryStats.map((cat) => {
                  if (!cat || !cat.category_name) return null; // Null check
                  
                  const Icon = CATEGORY_ICONS[cat.category_name] || BookOpen;
                  const colorClass = CATEGORY_COLORS[cat.category_name] || 'from-gray-500 to-gray-600';
                  
                  return (
                    <div key={cat.category_id} className="relative group">
                      <div className={`absolute -inset-1 bg-gradient-to-r ${colorClass} rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-300`}></div>
                      <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:scale-105 transition-all duration-300">
                        <div className="flex items-start justify-between mb-6">
                          <div className="flex items-center gap-4">
                            <div className={`p-4 bg-gradient-to-r ${colorClass} rounded-xl shadow-lg`}>
                              <Icon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-black text-white">{cat.category_name}</h3>
                              <p className="text-white/70">{cat.total_answered} soru çözüldü</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-4xl font-black ${
                              cat.accuracy_percentage >= 80 ? 'text-green-400' :
                              cat.accuracy_percentage >= 60 ? 'text-yellow-400' : 'text-orange-400'
                            }`}>
                              {cat.accuracy_percentage}%
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between text-sm text-white/80">
                            <span>✅ Doğru: <strong className="text-green-400">{cat.correct_answers}</strong></span>
                            <span>❌ Yanlış: <strong className="text-red-400">{cat.total_answered - cat.correct_answers}</strong></span>
                          </div>

                          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                            <div 
                              className={`h-3 rounded-full transition-all duration-1000 bg-gradient-to-r ${
                                cat.accuracy_percentage >= 80 ? 'from-green-400 to-emerald-500' :
                                cat.accuracy_percentage >= 60 ? 'from-yellow-400 to-orange-400' : 'from-orange-400 to-red-400'
                              }`}
                              style={{ width: `${cat.accuracy_percentage}%` }}
                            ></div>
                          </div>

                          <div className="pt-2 text-center">
                            {cat.accuracy_percentage >= 80 ? (
                              <span className="inline-flex items-center gap-1 text-green-400 font-semibold">
                                <Flame className="w-4 h-4" /> Mükemmel!
                              </span>
                            ) : cat.accuracy_percentage >= 60 ? (
                              <span className="inline-flex items-center gap-1 text-yellow-400 font-semibold">
                                <Star className="w-4 h-4" /> İyi gidiyorsunuz!
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-orange-400 font-semibold">
                                <Activity className="w-4 h-4" /> Daha fazla pratik yapın
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Marked Questions Tab */}
        {activeTab === 'marked' && (
          <div>
            <h2 className="text-3xl font-black text-white mb-6 flex items-center gap-3">
              <Bookmark className="w-8 h-8 text-yellow-400" />
              İşaretli Sorularınız ({markedQuestions.length})
            </h2>
            
            {markedQuestions.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 border border-white/20 text-center">
                <Bookmark className="w-20 h-20 text-white/50 mx-auto mb-4" />
                <p className="text-white/70 text-xl">Henüz işaretlenmiş soru yok.</p>
                <p className="text-white/50 mt-2">Pratik yaparken zorlandığınız soruları işaretleyebilirsiniz.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {markedQuestions.map((q) => (
                  <div key={q.id} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <Bookmark className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-white font-semibold text-lg mb-2">{q.question_text}</p>
                        <div className="flex gap-2 flex-wrap">
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm">
                            {categories.find(c => c.id === q.category_id)?.name_turkish || 'N/A'}
                          </span>
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm">
                            {q.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Exam History Tab */}
        {activeTab === 'exams' && examHistory.length > 0 && (
          <div>
            <h2 className="text-3xl font-black text-white mb-6 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-400" />
              Sınav Geçmişiniz
            </h2>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left text-white/80 font-bold py-4 px-4">Tarih</th>
                      <th className="text-left text-white/80 font-bold py-4 px-4">Tip</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">Sorular</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">Doğru</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">Yanlış</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">Skor</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">Durum</th>
                      <th className="text-center text-white/80 font-bold py-4 px-4">İncele</th>
                    </tr>
                  </thead>
                  <tbody>
                    {examHistory.slice(0, 10).map((exam, index) => {
                      const passed = exam.score_percentage >= 74;
                      return (
                        <tr 
                          key={exam.id || index} 
                          className="border-b border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                          onClick={() => setShowExamDetail(exam)}
                        >
                          <td className="py-4 px-4 text-white font-medium">
                            <span className="hover:text-blue-400 transition-colors underline decoration-dotted">
                              {new Date(exam.completed_at).toLocaleDateString('tr-TR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              exam.exam_type === 'exam' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                            }`}>
                              {exam.exam_type === 'exam' ? '🎯 Sınav' : '📚 Pratik'}
                            </span>
                          </td>
                          <td className="text-center py-4 px-4 text-white font-semibold">{exam.total_questions}</td>
                          <td className="text-center py-4 px-4 text-green-400 font-bold">{exam.correct_answers}</td>
                          <td className="text-center py-4 px-4 text-red-400 font-bold">{exam.wrong_answers}</td>
                          <td className="text-center py-4 px-4">
                            <span className={`font-black text-xl ${
                              exam.score_percentage >= 80 ? 'text-green-400' : 
                              exam.score_percentage >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              %{exam.score_percentage}
                            </span>
                          </td>
                          <td className="text-center py-4 px-4">
                            {passed ? (
                              <span className="inline-flex items-center gap-1 text-green-400 font-bold">
                                <CheckCircle className="w-5 h-5" /> Geçti
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-400 font-bold">
                                ❌ Kaldı
                              </span>
                            )}
                          </td>
                          <td className="text-center py-4 px-4">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowExamDetail(exam);
                              }}
                              className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 mx-auto"
                            >
                              <Eye className="w-4 h-4" />
                              Detay
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-white/50 text-sm mt-4 text-center">
                💡 Detayları görmek için herhangi bir satıra tıklayın
              </p>
            </div>
          </div>
        )}

        {/* Exam Detail Modal */}
        {showExamDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 max-w-2xl w-full mx-4 border border-white/20 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-yellow-400" />
                  Sınav Detayı
                </h3>
                <button
                  onClick={() => setShowExamDetail(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <XCircle className="w-6 h-6 text-white/60" />
                </button>
              </div>
              
              {/* Exam Summary */}
              <div className="bg-white/5 rounded-xl p-5 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {new Date(showExamDetail.completed_at).toLocaleDateString('tr-TR')}
                    </div>
                    <div className="text-white/50 text-sm">Tarih</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-400">{showExamDetail.correct_answers}</div>
                    <div className="text-white/50 text-sm">Doğru</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{showExamDetail.wrong_answers}</div>
                    <div className="text-white/50 text-sm">Yanlış</div>
                  </div>
                  <div>
                    <div className={`text-3xl font-black ${
                      showExamDetail.score_percentage >= 75 ? 'text-green-400' : 
                      showExamDetail.score_percentage >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      %{showExamDetail.score_percentage}
                    </div>
                    <div className="text-white/50 text-sm">Başarı</div>
                  </div>
                </div>
              </div>
              
              {/* Result Status */}
              <div className={`rounded-xl p-4 mb-6 text-center ${
                showExamDetail.score_percentage >= 75 
                  ? 'bg-green-500/20 border border-green-500/30' 
                  : 'bg-red-500/20 border border-red-500/30'
              }`}>
                {showExamDetail.score_percentage >= 75 ? (
                  <div className="flex items-center justify-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                    <span className="text-2xl font-bold text-green-400">TEBRİKLER! Sınavı Geçtiniz! 🎉</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <XCircle className="w-8 h-8 text-red-400" />
                    <span className="text-2xl font-bold text-red-400">Maalesef Geçemediniz 😔</span>
                  </div>
                )}
                <p className="text-white/60 mt-2">
                  Geçme sınırı: %75 | Sizin skorunuz: %{showExamDetail.score_percentage}
                </p>
              </div>
              
              {/* Performance Analysis */}
              <div className="bg-white/5 rounded-xl p-5 mb-6">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Performans Analizi
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">Doğru Cevap Oranı</span>
                      <span className="text-white font-semibold">{showExamDetail.correct_answers}/{showExamDetail.total_questions}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all"
                        style={{ width: `${(showExamDetail.correct_answers / showExamDetail.total_questions) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">Yanlış Cevap Oranı</span>
                      <span className="text-white font-semibold">{showExamDetail.wrong_answers}/{showExamDetail.total_questions}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-orange-400 rounded-full transition-all"
                        style={{ width: `${(showExamDetail.wrong_answers / showExamDetail.total_questions) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-200">
                    <p className="font-semibold mb-1">İpucu</p>
                    {showExamDetail.score_percentage >= 75 ? (
                      <p>Harika iş çıkardınız! Aynı performansı gerçek sınavda da gösterebilirsiniz. Pratik yapmaya devam edin!</p>
                    ) : (
                      <p>Yanlış yaptığınız konuları tekrar gözden geçirin. Her gün düzenli pratik yaparak başarı oranınızı artırabilirsiniz.</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Close Button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowExamDetail(null)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white font-bold rounded-xl transition-all"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ALL QUESTIONS TAB - Soru Bankası (Premium Only) */}
        {activeTab === 'allQuestions' && hasActiveSubscription && (
          <QuestionBank user={user} />
        )}

        {/* PERFORMANCE INSIGHTS TAB - Performans Analizi */}
        {activeTab === 'insights' && (
          <PerformanceInsights user={user} />
        )}
      </div>
      </div>
    </div>
  );
}

// Question Bank Component
function QuestionBank({ user }) {
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, bookmarked
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Pagination
  const [questionBankPage, setQuestionBankPage] = useState(1);
  const QUESTIONS_PER_PAGE = 50;
  
  // Track which questions have revealed answers
  const [revealedAnswers, setRevealedAnswers] = useState({});
  
  // Track user answers for each question - NEW
  const [userAnswers, setUserAnswers] = useState({});
  
  // Stats counters - NEW
  const [stats, setStats] = useState({ total: 0, correct: 0, wrong: 0 });
  
  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingQuestion, setReportingQuestion] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  
  // Handle answer selection - NEW
  const handleAnswerSelect = async (question, selectedAnswer) => {
    // Don't allow re-answering
    if (userAnswers[question.id]) return;
    
    const isCorrect = selectedAnswer === question.correct_answer;
    
    // Update local state immediately
    setUserAnswers(prev => ({
      ...prev,
      [question.id]: selectedAnswer
    }));
    
    // Update stats immediately
    setStats(prev => ({
      total: prev.total + 1,
      correct: prev.correct + (isCorrect ? 1 : 0),
      wrong: prev.wrong + (isCorrect ? 0 : 1)
    }));
    
    // Show the answer
    setRevealedAnswers(prev => ({
      ...prev,
      [question.id]: true
    }));
    
    // Save to backend immediately
    try {
      await axios.post(`${API}/user/answer`, null, {
        params: {
          question_id: question.id,
          user_answer: selectedAnswer
        },
        withCredentials: true
      });
      console.log('✅ Cevap kaydedildi:', question.id, isCorrect ? 'DOĞRU' : 'YANLIŞ');
    } catch (err) {
      console.error('Cevap kaydedilemedi:', err);
    }
  };
  
  const toggleRevealAnswer = (questionId) => {
    setRevealedAnswers(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };
  
  const copyQuestionId = (questionId) => {
    navigator.clipboard.writeText(questionId);
    alert(`Soru ID kopyalandı: ${questionId}`);
  };
  
  const openReportModal = (question) => {
    setReportingQuestion(question);
    setReportReason('');
    setShowReportModal(true);
  };
  
  const submitReport = async () => {
    if (!reportReason.trim()) {
      alert('Lütfen hata açıklaması girin');
      return;
    }
    
    setReportSubmitting(true);
    try {
      await axios.post(`${API}/questions/report`, {
        question_id: reportingQuestion.id,
        user_id: user?.id || user?.user_id,
        reason: reportReason,
        question_text: reportingQuestion.question_text?.substring(0, 100)
      });
      alert('Hata raporu gönderildi. Teşekkürler!');
      setShowReportModal(false);
    } catch (err) {
      console.error('Report error:', err);
      alert('Rapor gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setReportSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAllQuestions();
    fetchCategories();
    fetchBookmarks();
  }, []);

  const fetchAllQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if admin test mode
      const adminPassword = localStorage.getItem('admin_password');
      const storedUserId = localStorage.getItem('user_id');
      const storedUserEmail = localStorage.getItem('user_email');
      
      // Use user props as primary, localStorage as fallback
      const userEmail = user?.email || storedUserEmail;
      const userId = user?.user_id || user?.id || storedUserId;
      
      const config = {
        params: { user_id: userId },
        withCredentials: true,
        headers: {}
      };
      
      // Add admin password header if in admin test mode
      if (adminPassword) {
        config.headers['X-Admin-Password'] = adminPassword;
      }
      
      // Add user info headers for fallback authentication
      if (userEmail) {
        config.headers['X-User-Email'] = userEmail;
      }
      if (userId) {
        config.headers['X-User-Id'] = userId;
      }
      
      console.log('Fetching questions with config:', { 
        hasAdminPassword: !!adminPassword, 
        userId: user?.user_id || user?.id || storedUserId,
        userEmail: userEmail
      });
      
      const response = await axios.get(`${API}/questions/all`, config);
      
      if (Array.isArray(response.data)) {
        setAllQuestions(response.data);
        setError(null);
        console.log(`✅ ${response.data.length} soru yüklendi`);
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        setAllQuestions([]);
        setError('Beklenmeyen veri formatı');
      }
    } catch (error) {
      console.error('❌ Sorular yüklenemedi:', error?.response?.data || error.message);
      setAllQuestions([]);
      
      // Set specific error messages
      if (error?.response?.status === 401) {
        setError('🔒 Oturum sorunu - Lütfen çıkış yapıp tekrar giriş yapın');
      } else if (error?.response?.status === 403) {
        setError('🔒 Premium üyelik gerekli - Soru bankasına erişmek için premium üyelik satın alın');
      } else {
        setError('Sorular yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
      }
      
      setAllQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks/${user?.user_id || user?.id}`);
      setBookmarkedIds(response.data.question_ids || []);
    } catch (error) {
      console.error('İşaretliler yüklenemedi:', error);
      setBookmarkedIds([]);
    }
  };

  const toggleBookmark = async (questionId) => {
    try {
      const uid = user?.user_id || user?.id;
      if (bookmarkedIds.includes(questionId)) {
        // Remove bookmark
        await axios.delete(`${API}/bookmarks/${uid}/${questionId}`);
        setBookmarkedIds(bookmarkedIds.filter(id => id !== questionId));
      } else {
        // Add bookmark
        await axios.post(`${API}/bookmarks`, {
          user_id: uid,
          question_id: questionId
        });
        setBookmarkedIds([...bookmarkedIds, questionId]);
      }
    } catch (error) {
      console.error('İşaretleme hatası:', error);
      alert('İşaretleme sırasında hata oluştu');
    }
  };

  // Filter questions - Safe check
  const filteredQuestions = (allQuestions || []).filter(q => {
    if (filter === 'bookmarked' && !bookmarkedIds.includes(q.id)) return false;
    if (categoryFilter !== 'all' && q.category_id !== categoryFilter) return false;
    return true;
  });
  
  // Pagination for Soru Bankası
  const totalPages = Math.ceil(filteredQuestions.length / QUESTIONS_PER_PAGE);
  const paginatedQuestions = filteredQuestions.slice(
    (questionBankPage - 1) * QUESTIONS_PER_PAGE,
    questionBankPage * QUESTIONS_PER_PAGE
  );
  
  // Reset page when filters change
  useEffect(() => {
    setQuestionBankPage(1);
  }, [filter, categoryFilter]);

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-white"></div>
        <p className="text-white text-xl mt-4">Sorular yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* LIVE Stats Banner - NEW */}
      {stats.total > 0 && (
        <div className="bg-gradient-to-r from-purple-600/30 via-blue-600/30 to-green-600/30 backdrop-blur-lg rounded-2xl p-4 border border-white/20 animate-pulse">
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-3xl">📊</span>
              <div>
                <div className="text-white/60 text-sm">Bu Oturumda</div>
                <div className="text-white font-black text-xl">{stats.total} Soru</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">✅</span>
              <div>
                <div className="text-green-300 text-sm">Doğru</div>
                <div className="text-green-400 font-black text-xl">{stats.correct}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">❌</span>
              <div>
                <div className="text-red-300 text-sm">Yanlış</div>
                <div className="text-red-400 font-black text-xl">{stats.wrong}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">🎯</span>
              <div>
                <div className="text-yellow-300 text-sm">Başarı</div>
                <div className="text-yellow-400 font-black text-xl">
                  %{stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header & Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h2 className="text-3xl font-black text-white mb-2">📚 Soru Bankası</h2>
            <p className="text-white/80">
              Toplam {filteredQuestions.length} soru • {bookmarkedIds.length} işaretli
              {filteredQuestions.length > QUESTIONS_PER_PAGE && (
                <span className="ml-2 text-yellow-400">
                  • Sayfa {questionBankPage}/{totalPages}
                </span>
              )}
            </p>
          </div>
          
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => {
                setLoading(true);
                fetchAllQuestions();
              }}
              className="px-4 py-2 rounded-xl font-semibold transition-all bg-white/20 text-white hover:bg-white/30 flex items-center gap-2"
              title="Soruları Yenile"
            >
              <RefreshCw className="w-4 h-4" />
              Yenile
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                filter === 'all'
                  ? 'bg-white text-red-600'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Tümü
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
              categoryFilter === 'all'
                ? 'bg-white text-red-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Tüm Kategoriler
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                categoryFilter === cat.id
                  ? 'bg-white text-red-600'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {cat.name_turkish}
            </button>
          ))}
        </div>
      </div>

      {/* Pagination Controls - Top */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 flex-wrap">
          <button
            onClick={() => setQuestionBankPage(1)}
            disabled={questionBankPage === 1}
            className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            ⏮️ İlk
          </button>
          <button
            onClick={() => setQuestionBankPage(p => Math.max(1, p - 1))}
            disabled={questionBankPage === 1}
            className="px-4 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            ◀️ Önceki
          </button>
          
          {/* Page numbers */}
          <div className="flex gap-1">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (questionBankPage <= 3) {
                pageNum = i + 1;
              } else if (questionBankPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = questionBankPage - 2 + i;
              }
              
              return (
                <button
                  key={i}
                  onClick={() => setQuestionBankPage(pageNum)}
                  className={`w-10 h-10 rounded-lg font-bold transition-all ${
                    questionBankPage === pageNum
                      ? 'bg-white text-red-600'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setQuestionBankPage(p => Math.min(totalPages, p + 1))}
            disabled={questionBankPage === totalPages}
            className="px-4 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            Sonraki ▶️
          </button>
          <button
            onClick={() => setQuestionBankPage(totalPages)}
            disabled={questionBankPage === totalPages}
            className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            Son ⏭️
          </button>
        </div>
      )}

      {/* Questions Grid */}
      <div className="grid grid-cols-1 gap-4">
        {paginatedQuestions.map((question, idx) => {
          const isBookmarked = bookmarkedIds.includes(question.id);
          const globalIdx = (questionBankPage - 1) * QUESTIONS_PER_PAGE + idx;
          
          return (
            <div
              key={question.id}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    {/* Question ID - Always show */}
                    <div className="flex items-center gap-1">
                      <span className="bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 rounded-lg text-white font-black text-sm flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        {question.id || `Q-${String(globalIdx + 1).padStart(4, '0')}`}
                      </span>
                      <button
                        onClick={() => copyQuestionId(question.id)}
                        className="p-1 rounded hover:bg-white/10 transition-colors"
                        title="ID Kopyala"
                      >
                        <Copy className="w-3 h-3 text-white/50 hover:text-white" />
                      </button>
                    </div>
                    <span className="text-white/60 text-sm">
                      {categories.find(c => c.id === question.category_id)?.name_turkish || 'Kategori'}
                    </span>
                    {/* Report Button */}
                    <button
                      onClick={() => openReportModal(question)}
                      className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-sm transition-colors"
                      title="Yanlış Soru Bildir"
                    >
                      <Flag className="w-4 h-4" />
                      <span className="hidden sm:inline">Hata Bildir</span>
                    </button>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4 select-none" onContextMenu={(e) => e.preventDefault()}>
                    {question.question_text || question.question_text_turkish || 'Soru metni yok'}
                  </h3>
                  
                  {question.image_url && (
                    <div className="mb-4 flex justify-center">
                      <div className="relative bg-white/10 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-white/20 inline-block max-w-md">
                        <ImageLightbox
                          src={fixImageUrl(question.image_url)}
                          alt="Soru görseli"
                          className="rounded-lg max-h-48 w-auto object-contain"
                          containerClassName="relative"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2 mb-4 select-none" onContextMenu={(e) => e.preventDefault()}>
                    {(question.choices || []).map((choice, optIdx) => {
                      const isRevealed = revealedAnswers[question.id];
                      const isCorrect = choice.letter === question.correct_answer;
                      const userAnswer = userAnswers[question.id];
                      const isSelected = userAnswer === choice.letter;
                      const isWrongSelected = isSelected && !isCorrect;
                      const hasAnswered = !!userAnswer;
                      
                      return (
                        <button
                          key={optIdx}
                          onClick={() => !hasAnswered && handleAnswerSelect(question, choice.letter)}
                          disabled={hasAnswered}
                          className={`w-full p-4 rounded-xl transition-all select-none text-left flex items-center gap-3 ${
                            isRevealed && isCorrect
                              ? 'bg-green-500/30 border-2 border-green-400 text-white shadow-lg shadow-green-500/20'
                              : isWrongSelected
                              ? 'bg-red-500/30 border-2 border-red-400 text-white shadow-lg shadow-red-500/20'
                              : hasAnswered
                              ? 'bg-white/5 text-white/50 cursor-not-allowed'
                              : 'bg-white/5 text-white/80 hover:bg-white/15 hover:scale-[1.02] cursor-pointer border border-transparent hover:border-white/30'
                          }`}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          <span className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg ${
                            isRevealed && isCorrect
                              ? 'bg-green-500 text-white'
                              : isWrongSelected
                              ? 'bg-red-500 text-white'
                              : hasAnswered
                              ? 'bg-white/10 text-white/50'
                              : 'bg-white/20 text-white'
                          }`}>
                            {choice.letter}
                          </span>
                          <span className="flex-1">{choice.text}</span>
                          {isRevealed && isCorrect && (
                            <CheckCircle className="w-6 h-6 text-green-400" />
                          )}
                          {isWrongSelected && (
                            <XCircle className="w-6 h-6 text-red-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Show Answer Button - only if not answered */}
                  {!userAnswers[question.id] && !revealedAnswers[question.id] && (
                    <button
                      onClick={() => toggleRevealAnswer(question.id)}
                      className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white py-3 px-6 rounded-xl font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2 opacity-70 hover:opacity-100"
                    >
                      <Eye className="w-5 h-5" />
                      Cevabı Göster (Kayıt edilmez)
                    </button>
                  )}
                  
                  {/* Results after answering */}
                  {(userAnswers[question.id] || revealedAnswers[question.id]) && (
                    <div className="space-y-3">
                      <div className={`${userAnswers[question.id] === question.correct_answer ? 'bg-green-500/20 border-green-400/30' : 'bg-red-500/20 border-red-400/30'} border rounded-xl p-4`}>
                        <div className={`flex items-center gap-2 ${userAnswers[question.id] === question.correct_answer ? 'text-green-300' : 'text-red-300'} font-bold mb-2`}>
                          {userAnswers[question.id] === question.correct_answer ? (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              🎉 Doğru Cevap!
                            </>
                          ) : userAnswers[question.id] ? (
                            <>
                              <XCircle className="w-5 h-5" />
                              Yanlış! Doğru Cevap: {question.correct_answer}
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-300" />
                              Doğru Cevap: {question.correct_answer}
                            </>
                          )}
                        </div>
                      </div>
                      
                        <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
                        <div className="flex items-start gap-2 text-blue-200">
                          <Lightbulb className="w-5 h-5 flex-shrink-0 mt-1" />
                          <div>
                            <div className="font-bold mb-1">Açıklama:</div>
                            <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{question.explanation || question.explanation_turkish || 'Açıklama mevcut değil.'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => toggleBookmark(question.id)}
                  className={`p-3 rounded-xl transition-all hover:scale-110 ${
                    isBookmarked
                      ? 'bg-yellow-500 text-white'
                      : 'bg-white/10 text-white/50 hover:bg-white/20'
                  }`}
                >
                  <Bookmark
                    className={`w-6 h-6 ${isBookmarked ? 'fill-white' : ''}`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredQuestions.length === 0 && (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <p className="text-white/60 text-xl mb-4">
            {error ? error : (filter === 'bookmarked'
              ? 'Henüz işaretlenmiş soru yok'
              : 'Bu filtrelerle eşleşen soru bulunamadı')}
          </p>
          {error && (
            <button
              onClick={() => {
                setLoading(true);
                fetchAllQuestions();
              }}
              className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-5 h-5" />
              Tekrar Dene
            </button>
          )}
        </div>
      )}
      
      {/* Report Question Modal */}
      {showReportModal && reportingQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-red-950 to-red-900 rounded-2xl p-6 max-w-lg w-full border border-red-500/30 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Flag className="w-6 h-6 text-yellow-400" />
              <h3 className="text-xl font-bold text-white">Yanlış Soru Bildir</h3>
            </div>
            
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <div className="text-white/60 text-sm mb-1">Soru ID: {reportingQuestion.id}</div>
              <div className="text-white text-sm">{reportingQuestion.question_text?.substring(0, 100)}...</div>
            </div>
            
            <div className="mb-4">
              <label className="text-white/80 text-sm block mb-2">Hata Açıklaması:</label>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Lütfen hatayı detaylı olarak açıklayın..."
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-red-400 h-32 resize-none"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition-all"
              >
                İptal
              </button>
              <button
                onClick={submitReport}
                disabled={reportSubmitting || !reportReason.trim()}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
              >
                {reportSubmitting ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Pagination Controls - Bottom */}
      {totalPages > 1 && filteredQuestions.length > 0 && (
        <div className="flex justify-center items-center gap-2 flex-wrap mt-6">
          <button
            onClick={() => setQuestionBankPage(1)}
            disabled={questionBankPage === 1}
            className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            ⏮️ İlk
          </button>
          <button
            onClick={() => setQuestionBankPage(p => Math.max(1, p - 1))}
            disabled={questionBankPage === 1}
            className="px-4 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            ◀️ Önceki
          </button>
          
          <span className="px-4 py-2 text-white font-bold">
            Sayfa {questionBankPage} / {totalPages}
          </span>
          
          <button
            onClick={() => setQuestionBankPage(p => Math.min(totalPages, p + 1))}
            disabled={questionBankPage === totalPages}
            className="px-4 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            Sonraki ▶️
          </button>
          <button
            onClick={() => setQuestionBankPage(totalPages)}
            disabled={questionBankPage === totalPages}
            className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-all"
          >
            Son ⏭️
          </button>
        </div>
      )}
    </div>
  );
}
